import { useCallback, useState } from 'react'
import {
  clearPestPacLocationDetail,
  fetchEntityDetailAndProducts,
  selectMapEntityDetails,
  selectServiceLocationsDetails,
  setEntityDetails,
  setPestPacLocationPin,
  setSelectedEntityStatus,
  setSelectedServiceLocationId,
  setSelectedServiceLocationsDetails,
} from '@user/store/map'
import { MapPinVariant } from '@user/components/HereMap/MapPin/types'
import { useAppDispatch, useAppSelector } from '@user/store/hooks'
import { googleMapsOpenInMapsLink } from '@user/components/HereMap/utils/googleMapsLinks'
import { MultiTenantLocationDetails } from '@user/store/map/typings'
import { updateKnockStatusHistoryById } from '@user/store/knockStatus/api'
import { toast } from '@sales-center/components'
import { buildKnockStatusReasonPatches } from '@user/utils/knockStatusReasonPatch'

const mapVariantToEntityType = (variant: string): string => {
  switch (variant.toLowerCase()) {
    case 'opportunity':
      return 'Opportunity'
    case 'lead':
      return 'Lead'
    case 'pendinglead':
    case 'draftlead':
      return 'DraftLead'
    default:
      return 'Unknown'
  }
}

// Module-level so a slower request from another row can be told it's stale once a newer
// one starts, preventing it from opening its modal over that row's already-shown data.
let latestEntityDetailsRequestId = 0

function primeEntityDetails(
  dispatch: ReturnType<typeof useAppDispatch>,
  variant: string,
  entityId: number,
  serviceLocationId: number,
) {
  const requestId = ++latestEntityDetailsRequestId
  dispatch(setSelectedEntityStatus('loading'))
  dispatch(setSelectedServiceLocationId(serviceLocationId))
  const fetchPromise = dispatch(
    fetchEntityDetailAndProducts([
      variant.toLowerCase() as MapPinVariant,
      entityId,
      serviceLocationId!,
    ]),
  )
  return {
    fetchPromise,
    isLatest: async () => {
      await fetchPromise
      return requestId === latestEntityDetailsRequestId
    },
  }
}

type Props = {
  serviceLocationId: number
  setOpenKnockStatusModal: (status: boolean) => void
  entityId: number
  variant: string
  setDrawerOpen: (open: boolean) => void
  setIsLoading: (open: boolean) => void
  formattedPhone: string
  setShowHistory: (show: boolean) => void
  addressDisplay: string | null
  onReviewPendingLead?: (draftId: string) => void
  location?: MultiTenantLocationDetails
  onViewKnockStatusHistory?: (location: any) => void
  knockStatusHistoryId?: number | null
  knockStatusLocationId?: number | null
}

export const useHooks = ({
  serviceLocationId,
  setOpenKnockStatusModal,
  entityId,
  variant,
  setDrawerOpen,
  formattedPhone,
  setIsLoading,
  setShowHistory,
  addressDisplay,
  onReviewPendingLead,
  location,
  onViewKnockStatusHistory,
  knockStatusHistoryId,
  knockStatusLocationId,
}: Props) => {
  const dispatch = useAppDispatch()
  const serviceLocationsDetails = useAppSelector(selectServiceLocationsDetails)
  const entityDetails = useAppSelector(selectMapEntityDetails)
  const [isSavingReason, setIsSavingReason] = useState(false)

  const isDraftLead =
    variant.toLowerCase() === 'draftlead' ||
    variant.toLowerCase() === 'pendinglead'

  const handleSaveReason = useCallback(
    async (newReason: string | null) => {
      if (!knockStatusHistoryId) return false
      const entityType = mapVariantToEntityType(variant)
      if (entityType === 'Unknown') {
        toast.error('Failed to update reason')
        return false
      }
      setIsSavingReason(true)
      try {
        await dispatch(
          updateKnockStatusHistoryById({
            id: knockStatusHistoryId,
            entityType,
            input: {
              customReason: newReason,
              knockStatusName: null,
              previousKnockStatusName: null,
              entityType,
              entityId,
            },
          }),
        ).unwrap()

        // Patches BOTH the multi-tenant selectedServiceLocationsDetails slice
        // AND the single-entity entityDetails slice, so if a user has this
        // list and the single-entity drawer/bubble open at once for the same
        // record, both stay in sync live rather than one going stale until
        // its next refetch.
        const { patchedEntityDetails, patchedServiceLocationsDetails } =
          buildKnockStatusReasonPatches({
            entityId,
            serviceLocationId,
            isDraftLead,
            newReason,
            entityDetails,
            serviceLocationsDetails: serviceLocationsDetails.locations,
            knockStatusLocationId,
          })

        if (patchedServiceLocationsDetails) {
          dispatch(
            setSelectedServiceLocationsDetails(patchedServiceLocationsDetails),
          )
        }
        if (patchedEntityDetails) {
          dispatch(setEntityDetails(patchedEntityDetails))
        }

        toast.success('Reason updated')
        return true
      } catch (e: any) {
        toast.error(e?.message || 'Failed to update reason')
        return false
      } finally {
        setIsSavingReason(false)
      }
    },
    [
      knockStatusHistoryId,
      variant,
      entityId,
      serviceLocationId,
      isDraftLead,
      serviceLocationsDetails,
      entityDetails,
      knockStatusLocationId,
      dispatch,
    ],
  )

  const onViewMoreClick = useCallback(() => {
    dispatch(clearPestPacLocationDetail())
    dispatch(setPestPacLocationPin(null))
    primeEntityDetails(dispatch, variant, entityId, serviceLocationId)
    setDrawerOpen(true)
  }, [dispatch, entityId, serviceLocationId, setDrawerOpen, variant])

  const handleKnockStatusModalOpen = useCallback(async () => {
    setIsLoading(true)
    const { isLatest } = primeEntityDetails(
      dispatch,
      variant,
      entityId,
      serviceLocationId,
    )
    const stillLatest = await isLatest()
    setIsLoading(false)
    if (!stillLatest) return
    setShowHistory(false)
    setOpenKnockStatusModal(true)
  }, [
    dispatch,
    entityId,
    serviceLocationId,
    setIsLoading,
    setOpenKnockStatusModal,
    setShowHistory,
    variant,
  ])

  // useDeleteHistory/onUpdate key off Redux's entityDetails/variant/selectedServiceLocationId,
  // not this row's data, so this must prime them first or it'd act on a sibling row's record.
  const handleViewKnockStatusHistoryClick = useCallback(async () => {
    if (!onViewKnockStatusHistory || !location?.contactLocationId) return
    setIsLoading(true)
    const { isLatest } = primeEntityDetails(
      dispatch,
      variant,
      entityId,
      serviceLocationId,
    )
    const stillLatest = await isLatest()
    setIsLoading(false)
    if (!stillLatest) return
    onViewKnockStatusHistory({
      latitude: location.contact?.latitude ?? 0,
      longitude: location.contact?.longitude ?? 0,
      serviceLocationId: serviceLocationId || undefined,
      entityId: location.entityId,
      entityType: location.entityType,
      contactLocationId: location.contactLocationId,
    })
  }, [
    dispatch,
    entityId,
    serviceLocationId,
    setIsLoading,
    variant,
    onViewKnockStatusHistory,
    location,
  ])

  const handleKnockStatusModalClose = useCallback(() => {
    setShowHistory(false)
    setOpenKnockStatusModal(false)
  }, [setOpenKnockStatusModal, setShowHistory])

  const onPhoneNumberClick = useCallback(() => {
    if (formattedPhone) {
      window.open(`tel:${formattedPhone}`, '_blank')
    }
  }, [formattedPhone])

  const onViewDetails = useCallback(() => {
    if (
      (variant === 'pendinglead' || variant === 'draftlead') &&
      onReviewPendingLead
    ) {
      onReviewPendingLead(String(entityId))
      return
    }

    let path = ''
    switch (variant) {
      case 'lead':
        path = `/#/leads/${entityId}`
        break
      case 'opportunity':
        path = `/#/opportunities/${entityId}`
        break
      case 'pendinglead':
      case 'draftlead':
        path = `/#/leads/addLead?draftId=${entityId}`
        break
      default:
        break
    }

    if (path) window.open(path, '_blank', 'noopener,noreferrer')
  }, [entityId, variant, onReviewPendingLead])

  const onAddressMapsClick = useCallback(() => {
    if (!addressDisplay) {
      return
    }
    const url = googleMapsOpenInMapsLink(addressDisplay)
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [addressDisplay])

  return {
    onViewMoreClick,
    handleKnockStatusModalOpen,
    handleKnockStatusModalClose,
    handleViewKnockStatusHistoryClick,
    onViewDetails,
    onPhoneNumberClick,
    onAddressMapsClick,
    handleSaveReason,
    isSavingReason,
  }
}
